import { useSuspenseQuery } from '@apollo/client'
import { FetchViewGroupsDocument, type ViewFieldsFragment } from '@salescore/client-api'
import { getOrganizationIdFromPath } from '@salescore/client-base'
import { FolderPathAndCreator, HandleQuery, searchParentViewGroups } from '@salescore/client-common'
import { queueTimeWindowExecutorV2 } from '@salescore/frontend-common'
import { Select } from 'antd'
import { Array, Order, pipe, Record, String } from 'effect'
import { t } from 'i18next'
import { type ReactNode, useEffect, useMemo, useState } from 'react'

interface SelectOption {
  value: string | null
  label: string
  path: string[]
  createdBy: string | undefined
  searchKey: string
}

interface Properties {
  // eslint-disable-next-line sonarjs/todo-tag
  // TODO: undefinedはありえないはずだが、ViewForm側のロジックがいまいちで型の上だとundefinedになっている。ViewForm側を直したい。
  //       また、本来はViewGroupFieldsFragmentにしたいが、formが渡されうるのでこうしている
  type: ViewFieldsFragment['type'] | undefined
  isPrivate: boolean // privateという命名にしたいが、予約語なので使えない
  archived: boolean
  ignoreViewGroupIds?: string[]
  disabled?: boolean
  placeholder?: string
  value?: unknown // Form.Itemと一緒に使うのに必要 https://ant.design/components/form#formitemusestatus
  onChange?: (value?: unknown) => void
  hideFolder?: boolean
  ignorePrivateFlag?: boolean // privateのtrue/falseに関わらず全てのgroupを表示する(isPrivateを任意パラメータにした方が良いかも)
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
export function ViewGroupPicker(properties: Omit<Properties, 'viewGroups'>): ReactNode {
  return (
    <HandleQuery loadingElement={<Select loading />}>
      <Body {...properties} />
    </HandleQuery>
  )
}

// eslint-disable-next-line sonarjs/prefer-read-only-props
function Body({
  type,
  isPrivate,
  archived,
  ignoreViewGroupIds,
  disabled,
  placeholder,
  value,
  onChange,
  hideFolder,
  ignorePrivateFlag,
}: Properties): ReactNode {
  // 他のところでrefetch漏れがあって古いフォルダ構造を参照してしまうのを防ぐため、都度最新の値をここで取得
  // 特にサイドバーのドラッグアンドドロップに追従するために必要
  // （サイドバーのD&Dについては、D&Dの後に都度refetchすればいいのだが、refetchしていたら本番でのみ不可解な挙動をしたので、いったん利用をやめている）
  const {
    data: { viewGroups },
  } = useSuspenseQuery(FetchViewGroupsDocument, {
    variables: {
      organizationId: getOrganizationIdFromPath(),
    },
    fetchPolicy: 'cache-and-network',
  })
  const viewGroupsWithParents = useMemo(() => {
    const viewGroupsMap = Record.fromIterableBy(Array.reverse(viewGroups), (x) => x.id)
    return viewGroups.map((viewGroup) => ({
      viewGroup,
      parents: searchParentViewGroups(viewGroup.viewGroupId, viewGroupsMap),
    }))
  }, [viewGroups])

  const options: SelectOption[] = useMemo(() => {
    const xs = pipe(
      viewGroupsWithParents,
      Array.filter(
        (x) =>
          x.viewGroup.type === type &&
          (ignorePrivateFlag === true || x.viewGroup.private === isPrivate) &&
          x.viewGroup.archived === archived &&
          !(ignoreViewGroupIds ?? []).includes(x.viewGroup.id) &&
          Array.intersection(
            ignoreViewGroupIds ?? [],
            x.parents.map((parent) => parent.id),
          ).length === 0,
      ),
      Array.sortBy(
        Order.flip(
          Order.mapInput(
            Order.make<string>((a, b) => String.localeCompare(b)(a)),
            (x) => x.viewGroup.createdAt,
          ),
        ),
      ),
      Array.map((x): SelectOption => ({
        value: x.viewGroup.id,
        label: x.viewGroup.name,
        path: x.parents.map((x) => x.name).toReversed(),
        createdBy: x.viewGroup.createdBy?.name,
        searchKey: [x.viewGroup.name, ...x.parents.map((x) => x.name), x.viewGroup.createdBy?.name]
          .join('-')
          .toLowerCase(),
      })),
    )

    return [
      {
        value: null,
        label: t(`所属なし`),
        path: [],
        createdBy: '',
        searchKey: '',
      },
      ...xs,
    ]
  }, [viewGroupsWithParents])

  // ソート条件を高度にカスタマイズするため、
  // antdのfilterではなく、自前のフィルタロジックを使う
  const [filteredOptions, setFilteredOptions] = useState(options)
  const [isSearching, setIsSearching] = useState(false)
  const filter = (key: string): SelectOption[] => {
    if (key === '') {
      return options
    }
    const filteredOptions = options.filter((x) => x.searchKey.includes(key.toLowerCase()))
    // ソートを行う
    return pipe(
      filteredOptions,
      Array.sortBy(
        Order.combine(
          Order.mapInput(Order.Number, (a) => (a.label.includes(key) ? a.label.length : 99_999)), // label側で一致しているか。一致していれば、ラベルの文字長が短い順(=類似度が高い順)
          Order.mapInput(Order.Number, (a) => a.searchKey.length),
        ),
      ),
    )
  }
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setFilteredOptions(options)
  }, [options])
  const selectedOption = options.find((x) => x.value === value)

  return (
    <div>
      <Select
        value={value}
        disabled={disabled}
        placeholder={placeholder ?? t(`フォルダを選択`)}
        showSearch
        style={{ width: '100%' }}
        onChange={onChange}
        // ソート条件を高度にカスタマイズするため、
        // antdのfilterではなく、自前のフィルタロジックを使う
        filterOption={() => true}
        onSearch={(searchKey) => {
          const isSearchingNew = searchKey !== ''
          if (isSearchingNew !== isSearching) {
            setIsSearching(isSearchingNew)
          }

          queueTimeWindowExecutorV2({
            key: 'onSearch',
            f: () => {
              const filteredOptions = filter(searchKey)
              setFilteredOptions(filteredOptions)
            },
          })
        }}
        optionLabelProp="label"
      >
        {filteredOptions.map((x, index) => (
          <Select.Option key={index} {...x}>
            <div>
              <div className="whitespace-pre-wrap break-words pb-1">{x.label}</div>
              <FolderPathAndCreator path={x.path} createdBy={x.createdBy} />
            </div>
          </Select.Option>
        ))}
      </Select>
      {hideFolder === true ? null : (
        <div className="px-3">
          <FolderPathAndCreator path={selectedOption?.path} createdBy={selectedOption?.createdBy} />
        </div>
      )}
    </div>
  )
}
